import { BALANCE, BUILDING_DEFS, COLS } from '../constants';
import type { Simulation } from '../sim';
import type { SimState } from '../types';
import { FLAG_PAVED, FLAG_ROAD, GOOD_KINDS } from '../types';
import { settleResidentFamily, vacantFamilyCapacity } from './families';
import { pushLedger } from './economy';

export function isIncorporated(sim: Simulation): boolean {
  return sim.state.meta.townName !== null;
}

/** One clause of the Land Office's checklist, with the player's progress. */
export interface CharterRequirement {
  id: 'families' | 'population' | 'school' | 'hospital' | 'community';
  /** The clause as the Land Office words it. */
  label: string;
  met: boolean;
  /** "2/3" for the counted clauses, null for the built-and-running ones. */
  progress: string | null;
  /** What to do about it next — only meaningful while unmet. */
  hint: string;
}

/**
 * The incorporation checklist, evaluated. The single source of truth for both
 * charterReady() and the HUD's locked charter button — the requirements are
 * never stated anywhere else, so they must not drift from each other.
 */
export function charterRequirements(sim: Simulation): CharterRequirement[] {
  const state = sim.state;
  // One pass: how many of each civic building exist, and how many run.
  const built = { school: 0, hospital: 0, community: 0 };
  const running = { school: 0, hospital: 0, community: 0 };
  for (const building of state.buildings) {
    const slot =
      building.type === 'school'
        ? 'school'
        : building.type === 'hospital'
          ? 'hospital'
          : BUILDING_DEFS[building.type].service === 'community'
            ? 'community'
            : null;
    if (slot === null) continue;
    built[slot] += 1;
    if (building.functional) running[slot] += 1;
  }
  const civic = (
    id: 'school' | 'hospital' | 'community',
    label: string,
    buildHint: string,
  ): CharterRequirement => ({
    id,
    label,
    met: running[id] > 0,
    progress: null,
    hint:
      running[id] > 0
        ? ''
        : built[id] > 0
          ? 'Standing, but not open — it needs staff, power, and water.'
          : buildHint,
  });

  const households = state.households.length;
  const population = state.peeps.length;
  return [
    {
      id: 'families',
      label: 'Families in residence',
      met: households >= BALANCE.charterHouseholds,
      progress: `${households}/${BALANCE.charterHouseholds}`,
      hint: 'Build family housing, then approve the family requests that arrive in the mail.',
    },
    {
      id: 'population',
      label: 'Souls on the register',
      met: population >= BALANCE.charterPop,
      progress: `${population}/${BALANCE.charterPop}`,
      hint: 'Hire crew and keep housing, food, and water ahead of them — people follow the work.',
    },
    civic('school', 'A school, open', 'Build a School and assign it a teacher.'),
    civic('hospital', 'Physician services', 'Build a Hospital and assign it staff.'),
    civic('community', 'A house of community', 'Build a Chapel or Community Hall and assign it staff.'),
  ];
}

export function charterReady(sim: Simulation): boolean {
  if (sim.state.meta.townName !== null) return false;
  return charterRequirements(sim).every((requirement) => requirement.met);
}

/** Daily: how much anyone would WANT to live here (0..1). */
export function stepTownDaily(sim: Simulation): void {
  const state = sim.state;
  let amenities = 0;
  for (const building of state.buildings) {
    if (!building.functional) continue;
    amenities += BUILDING_DEFS[building.type].attract ?? 0;
  }
  amenities = Math.min(0.55, amenities);

  let happiness = 0.5;
  if (state.households.length > 0) {
    happiness = state.households.reduce((sum, household) => sum + household.happiness, 0) / state.households.length;
  }

  let roadTiles = 0;
  let pavedTiles = 0;
  for (let col = 0; col < COLS; col += 1) {
    const flags = state.terrain.flags[col] ?? 0;
    if (flags & FLAG_ROAD) {
      roadTiles += 1;
      if (flags & FLAG_PAVED) pavedTiles += 1;
    }
  }
  const pavedShare = roadTiles > 0 ? pavedTiles / roadTiles : 0;

  const value =
    amenities +
    happiness * 0.2 +
    state.workforce.morale * 0.1 +
    state.workforce.familyNeeds.air * 0.1 +
    pavedShare * 0.05;
  state.town.attractiveness = Math.max(0, Math.min(1, value));
}

export interface TownIncome {
  taxes: number;
  commerce: number;
  rail: number;
  interest: number;
  retail: number;
  power: number;
  passengers: number;
  total: number;
}

/** Market-day retail: residents buy stored goods at a premium, cheapest
 * staples first. Returns cents earned; deducts the units sold. */
export function sellRetailGoods(state: SimState): number {
  const marketOpen = state.buildings.some((building) => building.type === 'goodsMarket' && building.functional);
  if (!marketOpen) return 0;
  let demand = state.peeps.length * BALANCE.retailUnitsPerPersonPerWeek;
  const byPrice = [...GOOD_KINDS].sort(
    (a, b) => state.economy.market[a].price - state.economy.market[b].price || GOOD_KINDS.indexOf(a) - GOOD_KINDS.indexOf(b),
  );
  let earned = 0;
  for (const good of byPrice) {
    if (demand <= 0) break;
    const sold = Math.min(demand, state.economy.goodsDepot.units[good]);
    if (sold <= 0) continue;
    state.economy.goodsDepot.units[good] -= sold;
    demand -= sold;
    earned += Math.round(sold * state.economy.market[good].price * BALANCE.retailPremium);
  }
  return earned;
}

/** Weekly fares once a staffed platform sits on a working line. */
export function passengerIncomeCents(attractiveness: number, pop: number): number {
  return Math.round(BALANCE.passengerBaseCents * (0.5 + attractiveness * 1.5) * Math.min(1, pop / 50));
}

/** Weekly (from payroll): the income that doesn't come out of the ground. */
export function collectTownIncomeWeekly(sim: Simulation): TownIncome {
  const state = sim.state;
  const income: TownIncome = {
    taxes: 0,
    commerce: 0,
    rail: 0,
    interest: 0,
    retail: 0,
    power: 0,
    passengers: 0,
    total: 0,
  };
  if (!isIncorporated(sim)) return income;
  income.retail = sellRetailGoods(state);
  // Surplus electricity sells to the territorial grid at the going rate.
  if (sim.powerSellableSurplus > 0) {
    income.power = Math.round(sim.powerSellableSurplus * state.economy.market.power.price);
  }
  const terminalOpen = state.buildings.some(
    (building) => building.type === 'passengerTerminal' && building.functional,
  );
  const railOpenForPassengers = state.buildings.some(
    (building) => building.type === 'railDepot' && building.functional,
  );
  if (terminalOpen && railOpenForPassengers) {
    income.passengers = passengerIncomeCents(state.town.attractiveness, state.peeps.length);
  }

  for (const household of state.households) {
    income.taxes += Math.round(BALANCE.townTaxCentsPerWeek * (0.5 + household.happiness * 0.5));
  }
  income.commerce = Math.round(
    state.peeps.length * BALANCE.commerceCentsPerPersonPerWeek * state.town.attractiveness,
  );
  const railOpen = state.buildings.some((building) => building.type === 'railDepot' && building.functional);
  if (railOpen) {
    income.rail = Math.round(BALANCE.railIncomeBaseCents * (0.5 + state.town.attractiveness * 1.5));
  }
  const bankOpen = state.buildings.some((building) => building.type === 'bank' && building.functional);
  if (bankOpen && state.economy.cashCents > 0) {
    income.interest = Math.min(
      BALANCE.bankInterestCapCents,
      Math.round(state.economy.cashCents * BALANCE.bankInterestPerWeek),
    );
  }

  income.total =
    income.taxes + income.commerce + income.rail + income.interest + income.retail + income.power + income.passengers;
  state.economy.cashCents += income.total;
  const day = sim.day;
  if (income.taxes > 0) pushLedger(state, day, 'amenity', income.taxes, 'Town taxes');
  if (income.commerce > 0) pushLedger(state, day, 'amenity', income.commerce, 'Commerce');
  if (income.rail > 0) pushLedger(state, day, 'amenity', income.rail, 'Rail freight & passengers');
  if (income.interest > 0) pushLedger(state, day, 'amenity', income.interest, 'Bank interest');
  if (income.retail > 0) pushLedger(state, day, 'amenity', income.retail, 'Market day takings');
  if (income.power > 0) pushLedger(state, day, 'amenity', income.power, 'Power exports');
  if (income.passengers > 0) pushLedger(state, day, 'amenity', income.passengers, 'Passenger fares');
  return income;
}

/** Weekly: record trailing histories used by the endgame checks. Goods-truck
 * settlements accumulated during the week count on the town side. */
export function rollIncomeHistories(sim: Simulation, townTotalCents: number): void {
  const town = sim.state.town;
  town.mineIncomeHistory.push(town.weekMineIncomeCents);
  if (town.mineIncomeHistory.length > 4) town.mineIncomeHistory.shift();
  town.weekMineIncomeCents = 0;
  town.townIncomeHistory.push(townTotalCents + town.weekGoodsIncomeCents);
  if (town.townIncomeHistory.length > 4) town.townIncomeHistory.shift();
  town.weekGoodsIncomeCents = 0;
}

/** Weekly: attractive incorporated towns draw resident households on the bus. */
export function stepImmigrationWeekly(sim: Simulation): void {
  const state = sim.state;
  if (!isIncorporated(sim) || state.meta.gameOver) return;
  if (state.town.attractiveness < BALANCE.immigrationAttractMin) return;
  const capacity = vacantFamilyCapacity(sim);
  if (capacity <= 0) return;
  const strength =
    (state.town.attractiveness - BALANCE.immigrationAttractMin) / (1 - BALANCE.immigrationAttractMin);
  let arrivals = 0;
  const attempts = immigrationAttempts(capacity);
  for (let index = 0; index < attempts; index += 1) {
    if (arrivals >= capacity) break;
    if (sim.forks.families.chance(0.3 + strength * 0.5)) {
      settleResidentFamily(sim);
      arrivals += 1;
    }
  }
}

/** Immigration attempts/week grow with vacant housing — an attractive town
 * with empty apartments fills them fast; a full town gets the base trickle. */
export function immigrationAttempts(vacantCapacity: number): number {
  return Math.min(BALANCE.immigrationAttemptsMax, 2 + Math.floor(vacantCapacity / BALANCE.immigrationVacancyDivisor));
}
